import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SearchType } from 'fym-common/src/types/searchService';
import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { useLazySearchQuery } from '../../../../redux/api/musicAPI';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { ReleaseLinkData } from '../releaseLink/types';
import { catalogArtworkOriginalSrc } from '../../catalog/catalogArtwork';
import { parseCatalogTrackToReleaseLinks, parseSearchResults } from '../releaseLink/utils';
import { ReleaseResolution, emptyReleaseResolution } from './releaseResolutionTypes';
import { toReleaseDay } from './toReleaseDay';

/** Every source a found link belongs to, in the order they were found. */
function sourcesOf(links: ReleaseLinkData[]): MusicAPIIntegrationID[] {
  return links.reduce<MusicAPIIntegrationID[]>((sources, link) => {
    if (link.source !== undefined && !sources.includes(link.source)) {
      sources.push(link.source);
    }
    return sources;
  }, []);
}

/**
 * A catalog row turned into the links a release page needs, out of the row.
 *
 * The sibling of `useCatalogTrackResolution`, for the rows that have no catalog
 * track to read: a release claimed off the artist's own profile, or one they
 * added by hand. Both carry a synthetic key musicapi answers nothing for, and
 * both already hold the links the artist gave us — so the row is the source,
 * and only the services it does not cover are searched.
 *
 * A row with no links at all is an unreleased one. Nothing is resolved for it,
 * which is what the sheet has always done with it.
 */
export function useCatalogRowResolution(item: CatalogItem | undefined): ReleaseResolution {
  const [search] = useLazySearchQuery();

  // `undefined` while the search is still out; an array once it has answered,
  // empty included. That distinction is what "pending" is read from.
  const [searched, setSearched] = useState<ReleaseLinkData[]>();
  const startedForUuid = useRef<string | undefined>(undefined);

  const serviceLinks = item?.serviceLinks ?? null;
  const hasLinks = serviceLinks !== null && Object.keys(serviceLinks).length > 0;

  const known = useMemo(() => {
    if (item === undefined || serviceLinks === null || !hasLinks) {
      return undefined;
    }
    return parseCatalogTrackToReleaseLinks({
      mainName: item.title,
      mainAlbumName: null,
      mainArtistName: item.artistName,
      imageUrl: catalogArtworkOriginalSrc(item) ?? '',
      serviceLinks: Object.entries(serviceLinks).map(([integrationType, url]) => ({ integrationType, url })),
    });
  }, [hasLinks, item, serviceLinks]);

  useEffect(() => {
    if (item === undefined || known === undefined || startedForUuid.current === item.uuid) {
      return;
    }
    startedForUuid.current = item.uuid;
    setSearched(undefined);

    if (known.missingSources.length === 0) {
      setSearched([]);
      return;
    }
    search({
      track: item.title,
      artist: item.artistName,
      sources: known.missingSources,
      type: SearchType.track,
    })
      .then((searchResult) => {
        if (!searchResult.isSuccess) {
          setSearched([]);
          return;
        }
        const { tracks, albums } = searchResult.data;
        setSearched(parseSearchResults([...(tracks ?? []), ...(albums ?? [])]));
      })
      .catch(() => setSearched([]));
  }, [item, known, search]);

  const retry = useCallback(() => {
    startedForUuid.current = undefined;
    setSearched(undefined);
  }, []);

  return useMemo(() => {
    if (known === undefined || item === undefined) {
      return emptyReleaseResolution;
    }
    const links = [...known.releaseLinks, ...(searched ?? [])];
    const [main, ...others] = links;
    const foundSources = sourcesOf(links);
    const isSearching = searched === undefined;

    return {
      isResolving: isSearching,
      retry,
      main,
      others,
      foundSources,
      pendingSources: isSearching ? known.missingSources : [],
      missingSources: isSearching ? [] : known.missingSources.filter((source) => !foundSources.includes(source)),
      releaseDate: toReleaseDay(item.releaseDate ?? undefined),
      title: item.title,
      artistName: item.artistName,
      imageUrl: catalogArtworkOriginalSrc(item),
      // The row is the answer, so there is never nothing to route to.
      hasNoLinks: false,
    };
  }, [item, known, retry, searched]);
}
